import { useMemo, useRef } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { ApiError } from '@/lib/api'
import { QK, invalidateFor } from '@/lib/query-keys'
import { DsToast } from '@/ds'
import { notificationsApi } from '@/features/notifications/notifications.api'
import { notificationStrings as s } from '@/features/notifications/strings'
import { useUnreadCount, unreadCountKey } from '@/features/notifications/useUnreadCount'
import { NOTIFICATION_BUCKETS, notificationBucket, type NotificationBucket } from '@/features/notifications/notification-utils'
import type { AppNotification, NotificationListResponse } from '@/types/models'

export type NotificationFilter = 'all' | 'unread'
const PAGE = 25

export const notificationKeys = {
  list: (filter: NotificationFilter) => [...QK.notifications, 'list', filter] as const,
  preview: [...QK.notifications, 'preview'] as const,
}

type Pages = InfiniteData<NotificationListResponse, number>

/** Mark read is optimistic (the row flips, the badge drops before the PATCH returns); a failure refetches. */
export function useNotifications() {
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const filter: NotificationFilter = params.get('filter') === 'unread' ? 'unread' : 'all'
  // The API filters by read state only, so the type is applied to the loaded rows.
  const groupParam = params.get('group') as NotificationBucket | null
  const group: NotificationBucket | null = groupParam && NOTIFICATION_BUCKETS.includes(groupParam) ? groupParam : null
  const unreadCount = useUnreadCount()

  const query = useInfiniteQuery({
    queryKey: notificationKeys.list(filter),
    queryFn: ({ pageParam }) => notificationsApi.list({ page: pageParam, size: PAGE, ...(filter === 'unread' ? { read: false } : {}) }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.size < last.total ? last.page + 1 : undefined),
  })
  const loaded = useMemo(() => query.data?.pages.flatMap((p) => p.notifications) ?? [], [query.data])
  const items = useMemo(() => (group ? loaded.filter((n) => notificationBucket(n.type) === group) : loaded), [loaded, group])
  const total = query.data?.pages[0]?.total ?? 0

  // Two quick clicks land before the router re-renders, so each change starts from the last one
  // written, not from this render's params (which would put back what the first click removed).
  const latest = useRef({ seen: params, next: params })
  if (latest.current.seen !== params) latest.current = { seen: params, next: params }
  const updateParams = (edit: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(latest.current.next)
    edit(next)
    latest.current.next = next
    setParams(next, { replace: true })
  }
  const setFilter = (f: NotificationFilter) =>
    updateParams((next) => {
      if (f === 'all') next.delete('filter')
      else next.set('filter', f)
    })
  const setGroup = (g: NotificationBucket | null) =>
    updateParams((next) => {
      if (g) next.set('group', g)
      else next.delete('group')
    })
  const refreshPreview = () => void qc.invalidateQueries({ queryKey: notificationKeys.preview })

  const patchLists = (fn: (n: AppNotification) => AppNotification | null) => {
    for (const f of ['all', 'unread'] as const) {
      qc.setQueryData<Pages>(notificationKeys.list(f), (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((p) => {
                const notifications = p.notifications.map(fn).filter((n): n is AppNotification => n !== null)
                return { ...p, notifications, total: Math.max(0, p.total - (p.notifications.length - notifications.length)) }
              }),
            }
          : old,
      )
    }
  }

  const markRead = useMutation({
    mutationFn: (n: AppNotification) => (n.read ? Promise.resolve(null) : notificationsApi.markRead(n.id)),
    onMutate: (n) => {
      if (n.read) return
      qc.setQueryData<Pages>(notificationKeys.list('all'), (old) => (old ? { ...old, pages: old.pages.map((p) => ({ ...p, notifications: p.notifications.map((x) => (x.id === n.id ? { ...x, read: true } : x)) })) } : old))
      qc.setQueryData<Pages>(notificationKeys.list('unread'), (old) => (old ? { ...old, pages: old.pages.map((p) => ({ ...p, notifications: p.notifications.filter((x) => x.id !== n.id), total: Math.max(0, p.total - 1) })) } : old))
      qc.setQueryData<number>(unreadCountKey, (c) => Math.max(0, (c ?? 1) - 1))
    },
    onSuccess: refreshPreview,
    onError: () => void invalidateFor('notification'),
  })

  /** Bulk mark read: one PATCH per unread row, the API has no batch call. */
  const markManyRead = useMutation({
    mutationFn: (list: AppNotification[]) => Promise.all(list.filter((n) => !n.read).map((n) => notificationsApi.markRead(n.id))),
    onMutate: (list) => {
      const ids = new Set(list.filter((n) => !n.read).map((n) => n.id))
      if (ids.size === 0) return
      patchLists((n) => (ids.has(n.id) ? { ...n, read: true } : n))
      qc.setQueryData<Pages>(notificationKeys.list('unread'), (old) => (old ? { ...old, pages: old.pages.map((p) => { const notifications = p.notifications.filter((x) => !ids.has(x.id)); return { ...p, notifications, total: Math.max(0, p.total - (p.notifications.length - notifications.length)) } }) } : old))
      qc.setQueryData<number>(unreadCountKey, (c) => Math.max(0, (c ?? ids.size) - ids.size))
    },
    onSuccess: refreshPreview,
    onError: (e) => {
      DsToast.error(e instanceof ApiError ? e.message : s.somethingWentWrong)
      void invalidateFor('notification')
    },
  })

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      patchLists((n) => ({ ...n, read: true }))
      qc.setQueryData<Pages>(notificationKeys.list('unread'), (old) => (old ? { ...old, pages: old.pages.map((p) => ({ ...p, notifications: [], total: 0 })) } : old))
      qc.setQueryData<number>(unreadCountKey, 0)
      refreshPreview()
    },
    onError: (e) => DsToast.error(e instanceof ApiError ? e.message : s.somethingWentWrong),
  })

  return { ...query, items, loaded, total, filter, setFilter, group, setGroup, unreadCount, markRead, markManyRead, markAllRead }
}

export type NotificationsVm = ReturnType<typeof useNotifications>
